import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ScrollText, Lightbulb } from 'lucide-react';

const SectionIcon = ({ Icon, color, bg }) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 32,
      height: 32,
      borderRadius: 10,
      background: bg,
      color,
      marginRight: 10,
      verticalAlign: 'middle',
    }}
  >
    <Icon size={18} strokeWidth={2} />
  </span>
);

const Insights = () => {
  const { oppositionName } = useParams();
  const navigate = useNavigate();
  const [arts, setArts] = useState([]);
  const [concepts, setConcepts] = useState([]);
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const [a, c, p] = await Promise.all([
          axios.get(`/api/oposicion/${oppositionName}/key-articles?limit=30`),
          axios.get(`/api/oposicion/${oppositionName}/key-concepts?limit=40`),
          axios.get(`/api/oposicion/${oppositionName}/predictions?limit=20`).catch(() => ({ data: [] })),
        ]);
        setArts(a.data);
        setConcepts(c.data);
        setPredictions(p.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [oppositionName]);

  if (loading) return <div className="page-loader">Calculando insights…</div>;

  return (
    <div>
      <button
        onClick={() => navigate(`/oposicion/${oppositionName}`)}
        className="back-link"
      >
        ← Volver
      </button>

      <header className="page-header">
        <h1 className="page-title">Lo más importante</h1>
        <p className="page-subtitle">
          {oppositionName} · Los artículos y conceptos más relevantes del temario
        </p>
      </header>

      {predictions.length > 0 && (
        <div className="card" style={{ marginBottom: 20 }}>
          <h3 className="section-title" style={{ marginBottom: 12 }}>
            Predicción para el próximo examen
          </h3>
          <p style={{ fontSize: 12, color: '#666', margin: '0 0 12px' }}>
            Combina la relevancia del artículo con las veces que ha caído en examen, ordenados de más a menos probable.
          </p>
          <table className="admin-table">
            <thead><tr><th>#</th><th>Artículo</th><th>Ley</th><th>Veces preguntado</th></tr></thead>
            <tbody>
              {predictions.map((p, i) => (
                <tr key={i}>
                  <td>{i + 1}</td>
                  <td>
                    <Link to={`/article/${encodeURIComponent(p.law)}/${encodeURIComponent(p.number)}`}>
                      {p.number}
                    </Link>
                  </td>
                  <td>{p.law}</td>
                  <td className="num-cell">{p.times}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <div className="card">
          <h3 className="section-title" style={{ marginBottom: 12, display: 'flex', alignItems: 'center' }}>
            <SectionIcon Icon={ScrollText} color="#6b3fa0" bg="#f3eefb" />
            Top {arts.length} artículos clave
          </h3>
          <p style={{ fontSize: 12, color: '#666', margin: '0 0 12px' }}>
            Los que más se relacionan con el resto del temario. Conviene dominarlos primero.
          </p>
          <ol style={{ paddingLeft: 20, margin: 0 }}>
            {arts.map((a, i) => (
              <li key={i} style={{ marginBottom: 8, fontSize: 14 }}>
                <Link
                  to={`/article/${encodeURIComponent(a.law)}/${encodeURIComponent(a.number)}`}
                  style={{ color: '#0067b8', textDecoration: 'none', fontWeight: 500 }}
                >
                  {a.number}
                </Link>
                <span style={{ color: '#666', marginLeft: 8 }}>· {a.law}</span>
              </li>
            ))}
          </ol>
        </div>

        <div className="card">
          <h3 className="section-title" style={{ marginBottom: 12, display: 'flex', alignItems: 'center' }}>
            <SectionIcon Icon={Lightbulb} color="#d97706" bg="#fffbeb" />
            Top {concepts.length} conceptos clave
          </h3>
          <p style={{ fontSize: 12, color: '#666', margin: '0 0 12px' }}>
            Los términos que se repiten una y otra vez a lo largo del temario.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {concepts.map((c, i) => (
              <span
                key={i}
                style={{
                  padding: '6px 12px',
                  background: '#f0f7fb',
                  color: '#0067b8',
                  borderRadius: 14,
                  fontSize: 13,
                  fontWeight: 500,
                  border: '1px solid #d6e7f3',
                }}
                title={`Relevancia: ${c.pr.toFixed(6)}`}
              >
                {c.name}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Insights;
